import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as THREE from 'three';
import * as THREESTLLoader from 'three-stl-loader';
import * as THREETrackBallController from 'three-trackballcontrols';
import * as THREEObjLoader from 'three-obj-loader';
import * as OBJLoader from 'three-obj-loader';
import {CAMERA_CONFIG, COLORS_CONFIG, LIGHTS_CONFIG, SCENES_CONFIG} from '../../util/three/three.config';
import {Scenes, ThreeCanvasDimensions} from '../../util/three/three.interface';
import {ThreeUtil} from '../../util/three/three.util';
import {BiopsyMeshData, I3DTooltip, ProstateMeshData, RoiMeshData, ViewControls} from '../../interfaces/3dViewer';
import {get, has} from 'lodash';
import {IPCOSummaryTable, IPCOSummaryTableArray} from "@ispm/dynamic-form/interfaces/ISummaryTableInterface";

OBJLoader(THREE);
const HOMUNCULUS_OBJ_URL = '/assets/3dModel/3d-homunculus.obj';
@Component({
  selector: 'ispm-anatomy3d-viewer',
  templateUrl: './anatomy3d-viewer.component.html',
  styleUrls: ['./anatomy3d-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Anatomy3dViewerComponent implements OnInit,AfterViewInit, OnChanges,OnDestroy {


  private three;
    constructor(private ngZone: NgZone) {
      this.three = THREE;
  }

  ngOnInit() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.scroll.bind(this), true);
    });
  }
  @Input() public prostateMeshData: ProstateMeshData;
  @Input() public summryTable: IPCOSummaryTableArray;

  @Input() public width: number;
  @Output() public updateReport= new EventEmitter();

  @ViewChild('prostateViewer') private prostateViewer: ElementRef;
  private dimensions: ThreeCanvasDimensions;
  private scenes: Scenes;
  private camera: THREE.PerspectiveCamera;

  private matrix: THREE.Matrix4;
  private rotationMatrix: THREE.Matrix4;
  private prostateMeshBoundingBox: THREE.Box3;

  private renderer: THREE.WebGLRenderer;
  private controls: ViewControls;

  private rayCaster: THREE.Raycaster;
  private mouse : THREE.Vector2;
  private intersectionObjects:THREE.Object3D[] = [];
private scrollOffsetTop=0;
private reportData="";
private iterator=0;

  ngAfterViewInit(): void {
    const nativeElement = this.prostateViewer.nativeElement;
    this.getDimension(nativeElement);


  }
  public ngOnChanges(changes: SimpleChanges): void {
    if(this.prostateViewer) {


      const nativeElement = this.prostateViewer.nativeElement;
      this.getDimension(nativeElement);

    }
  }

  public getDimension(nativeElement){
    if (nativeElement) {
      this.dimensions = this.calculateDimensions(nativeElement);
      this.scenes = this.initializeScenes();
      this.camera = this.initializeCamera();


      this.decorateScenes();

      this.loadModels(this.prostateMeshData);

      this.renderer = this.createRenderer();
      nativeElement.appendChild(this.renderer.domElement);
      nativeElement.addEventListener('click', this.onMouseClick.bind(this), false);
      this.renderer.domElement.addEventListener('wheel', this.onWheel.bind(this), false);
      this.renderer.domElement.addEventListener('mousemove', this.onMouseOver.bind(this), false);
      this.renderer.domElement.addEventListener('mouseout', this.onMouseOut.bind(this), false);
      const track = THREETrackBallController;
      this.controls = this.setupCameraRotationWithMouse(track);
      this.reportData = this.renderer.domElement.toDataURL('image/png');
      this.animate();
      if (this.prostateViewer.nativeElement.childNodes.length > 1)
        this.prostateViewer.nativeElement.childNodes[0].remove();
    }
  }
//TODO Fix the clientwidth instead while used in form
  private calculateDimensions = (element: Element): ThreeCanvasDimensions => ({
    width: this.width ? this.width : element.clientWidth,
    height: element.clientHeight,
    aspect: (this.width ? this.width : element.clientWidth) / element.clientHeight,
  });

  private initializeScenes = (): Scenes => ({
    main: new this.three.Scene(),
    homunculus: new this.three.Scene(),
  });

  private initializeCamera(): THREE.PerspectiveCamera {
    const camera = new this.three.PerspectiveCamera(
      CAMERA_CONFIG.fov,
      this.dimensions.aspect,
      CAMERA_CONFIG.near,
      CAMERA_CONFIG.far);
    camera.position.z = CAMERA_CONFIG.position.z;
    return camera;
  }

  private decorateScenes(): void {
    const ambientLight = new this.three.AmbientLight(LIGHTS_CONFIG.ambient);
    const spotLight = this.getSpotLight();
    const plane = this.getPlane();
    ThreeUtil.addObjectToAllScenes(this.scenes, spotLight);
    ThreeUtil.addObjectToAllScenes(this.scenes, ambientLight);
    ThreeUtil.addObjectToAllScenes(this.scenes, plane);
  }

  private loadModels(prostateMeshData: ProstateMeshData): void {
    const loader = new THREESTLLoader(THREE);
    let dataUri=`data:text/plain;base64,${prostateMeshData.prostateMeshUrl}`;
    loader.prototype.load(dataUri, (geometry: THREE.Geometry) => {
      this.setModelMatricesAndProstateMeshBoundings(geometry);

      this.setupHomunculusMesh();

      const prostateMesh = this.createProstateMesh(geometry);
      let biopsyMeshes;
      if (prostateMeshData.biopsies) {
        biopsyMeshes = this.createBiopsyMeshes(prostateMeshData.biopsies);
      }
      if (prostateMeshData.rois){
        this.loadRoiMeshesOnScene(prostateMeshData.rois);
      }

      this.addToMainScene(prostateMesh);
      if (prostateMeshData.biopsies) {
        biopsyMeshes.forEach(biopsyMesh => this.addToMainScene(biopsyMesh));
      }
      this.scaleInView();
    });
  }

  private createRenderer(): THREE.WebGLRenderer {
    const renderer = new this.three.WebGLRenderer({ antialias: true ,preserveDrawingBuffer:true});
    renderer.autoClear = false;
    renderer.setClearColor(COLORS_CONFIG.general.background);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(this.dimensions.width, this.dimensions.height);
    renderer.sortObjects = false;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = this.three.PCFShadowMap;
    return renderer;
  }

  private setupCameraRotationWithMouse(TrackballControls): ViewControls {
    const controls = new TrackballControls(this.camera, this.renderer.domElement);
    controls.rotateSpeed = 5.0;
    controls.zoomSpeed = 1.2;
    controls.panSpeed = 0.8;
    controls.noZoom = false;
    controls.noPan = false;
    controls.staticMoving = true;
    controls.dynamicDampingFactor = 0.3;
    return controls;
  }

  private getPlane = (): THREE.Mesh => new THREE.Mesh(
    new this.three.PlaneBufferGeometry(2000, 2000, 8, 8),
    new this.three.MeshBasicMaterial({ visible: false })
  );

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    this.renderScenes();
  };

  private getSpotLight(): THREE.SpotLight {
    const spotLight = new THREE.SpotLight(LIGHTS_CONFIG.spot, 1.5);

    spotLight.position.set(0, 500, 2000);
    spotLight.castShadow = true;
    spotLight.shadow.camera.near = 200;
    spotLight.shadow.camera.far = this.camera.far;
    spotLight.shadow.camera.fov = 50;
    spotLight.shadow.bias = -0.00022;
    spotLight.shadow.mapSize.width = 2048;
    spotLight.shadow.mapSize.height = 2048;

    return spotLight;
  }

  private setModelMatricesAndProstateMeshBoundings(geometry: THREE.Geometry): void {
    geometry.computeFaceNormals();
    geometry.computeBoundingBox();

    // A transformation matrix (matrix) is applied to prostate segmentation, target and biopsy,
    // which contains a translation and a rotation
    // The translation shifts the center of segmentation mesh to view center
    this.prostateMeshBoundingBox = geometry.boundingBox.clone();
    const center = this.prostateMeshBoundingBox.getCenter(new this.three.Vector3());
    const translationMatrix = new this.three.Matrix4().makeTranslation(-center.x, -center.y, -center.z);

    // The default view (DICOM coordinate) is patient head pointing towards user.
    // We like patient foot pointing towards user.
    // Therefore a 180 degree rotation along x (left-right) axis is applied.
    const rotAxis = new this.three.Vector3(1, 0, 0);
    this.rotationMatrix = new this.three.Matrix4().makeRotationAxis(rotAxis, Math.PI);

    // matrix is the multiplication of rotation and translation
    this.matrix = new this.three.Matrix4().multiplyMatrices(this.rotationMatrix, translationMatrix);
  }

  private createProstateMesh(geometry: THREE.Geometry): THREE.Mesh {
    const shaderMaterial = ThreeUtil.createShaderMaterial(new this.three.Vector4(0.8, 0.6, 0.2, 0));

    geometry.computeFaceNormals();
    geometry.computeBoundingBox();

    // A transformation matrix (matrix) is applied to prostate segmentation, target and biopsy,
    // which contains a translation and a rotation
    // The translation shifts the center of segmentation mesh to view center
    this.prostateMeshBoundingBox = geometry.boundingBox.clone();
    const mesh = new this.three.Mesh(geometry, shaderMaterial);
    mesh.applyMatrix(this.matrix);
    mesh.name = 'prostate';

    return mesh;
  }

  private renderScenes(): void {
    this.controls.update();
    this.renderer.clear();
    this.renderer.setViewport(-150, 0, SCENES_CONFIG.homunculus.width, SCENES_CONFIG.homunculus.height);
    this.renderer.render(this.scenes.homunculus, this.camera);

    this.renderer.clearDepth();
    this.renderer.setViewport(0, 0, this.dimensions.width, this.dimensions.height);
    this.renderer.render(this.scenes.main, this.camera);
    let reportBuffer=this.renderer.domElement.toDataURL('image/png');
    //this is where we capture report data from canvas which is used in Actual report preview.
    if(Math.ceil(this.reportData.length/1024) !=Math.ceil(reportBuffer.length/1024)) {
      this.iterator++;
      let maxIteration =1+(this.prostateMeshData.rois?this.prostateMeshData.rois.length:0)+(this.prostateMeshData.biopsies?1:0);
      this.reportData=reportBuffer;
      if(!this.prostateMeshData.reportData && this.iterator>=maxIteration){
        this.prostateMeshData.reportData= this.reportData;
        this.updateReport.emit(this.prostateMeshData.reportData);
      }
      // console.log(reportBuffer);
    }
  }
private returnIntersection(event){
  this.rayCaster = new this.three.Raycaster();
  this.mouse = new this.three.Vector2();
  this.mouse.x = ((event.offsetX  / this.dimensions.width ) * 2) - 1;
  this.mouse.y = - ((event.offsetY/ this.dimensions.height) * 2) + 1;
  this.intersectionObjects = [];
  // update the picking ray with the camera and mouse position
  this.rayCaster.setFromCamera( this.mouse, this.camera );
  const meshes = this.scenes.main.children;


  // calculate objects intersecting the picking ray
  let intersects = this.rayCaster.intersectObjects( meshes ,true);

  for ( let i = 0; i < intersects.length; i++ ) {

    this.intersectionObjects[i] = intersects[ i ].object;

  }

}
  private onMouseClick(event){
    event.preventDefault();
    this.returnIntersection(event);
       if(this.intersectionObjects.length>0) {
console.log(`Click is not implemented .But intersection found.Details as below
${this.intersectionObjects}`);
    }
  }
  scroll = (event): void => {
      this.ngZone.run(() => {
        this.scrollOffsetTop=event.target.scrollTop;
      });
  }
  private onMouseOver(event) {
    let tooltipData: Array<I3DTooltip> =[];
    let hasData: boolean = false;
    event.preventDefault();
    this.returnIntersection(event);
    if (this.intersectionObjects.length > 0) {
      let showTooltip = false;
      this.intersectionObjects.forEach(intersects => {
        tooltipData = new Array<I3DTooltip>();
        if (intersects.name !== "prostate") {
          if (intersects.name.includes('roi_')) {
            let index = intersects.name.split('_')[1];
            this.prostateMeshData.rois.forEach(rois => {
              if (rois.ROIId == index) {
                showTooltip = true && get(rois, "tooltipConfiguration.tooltipShow");
                const isummaryTableItem = this.summryTable.summary.filter(item => item.target === rois.ROIName);
                hasData = isummaryTableItem.length > 0 ? true: false;
                tooltipData = this.createTooltipData(isummaryTableItem, tooltipData, false , rois.ROIName);
              }
            });
          } else if (intersects.name.includes('biopsy_')) {
            let index = intersects.name.split('_')[1];

            showTooltip = true && get(this.prostateMeshData.biopsies[index], "tooltipConfiguration.tooltipShow");
            const lesionID=  get(this.prostateMeshData.biopsies[index], "tooltipConfiguration.tooltipData.lesionID");
            const isummaryTableItem = this.summryTable.summary.filter(item => item.target === lesionID);
            hasData = isummaryTableItem.length > 0 ? true: false;
            if(isummaryTableItem.length > 0) {
              tooltipData = this.createTooltipData(isummaryTableItem, tooltipData, true);
            }
          }
        }
      })
      if (this.prostateViewer.nativeElement.childNodes.length > 1) {
        this.prostateViewer.nativeElement.childNodes[1].remove();
      }
      showTooltip ? this.createTooltip(event, tooltipData, hasData) : '';
    }
  }

  private createTooltipData(isummaryTableItem: IPCOSummaryTable[], tooltipData: Array<I3DTooltip>, isBiopsy: boolean, roiName?: string): Array<I3DTooltip> {
    if (isummaryTableItem.length > 0) {

      if (isummaryTableItem[0].target && !isummaryTableItem[0].isSystemicBiopsies) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].target, tooltipData, 'Target');
      }
      if (isummaryTableItem[0].target && isummaryTableItem[0].isSystemicBiopsies) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].target, tooltipData, 'Name');
      }
      if (isummaryTableItem[0].sampleLabel && isBiopsy) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].sampleLabel, tooltipData, 'Label');
      }
      if (isummaryTableItem[0].pirads && !isBiopsy) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].pirads, tooltipData, 'Pirads');
      }
      if (isummaryTableItem[0].biopsies && !isBiopsy) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].biopsies, tooltipData, 'Biopsies');
      }
      if (isummaryTableItem[0].overallGleason) {
        tooltipData = this.tooltipObject(isummaryTableItem[0].overallGleason, tooltipData, 'Gleason');
      }
    } else if (isummaryTableItem.length === 0) {
      tooltipData = this.tooltipObject(roiName, tooltipData, 'Target');
    }
    return tooltipData;
  }

  private tooltipObject(value : string, tooltipData: Array<I3DTooltip>, typeName : string) {
    const tooltipitem: I3DTooltip = new class implements I3DTooltip {
      type: string;
      value: string;
    }
    tooltipitem.type = typeName;
    tooltipitem.value = value;
    tooltipData = [...tooltipData, tooltipitem];
    return tooltipData;
  }

  private onMouseOut (event){
    event.preventDefault();
      if(this.prostateViewer.nativeElement.childNodes.length>1)
        this.prostateViewer.nativeElement.childNodes[1].remove();
  }

  private createTooltip(event:any,data, hasData: boolean) {
    let section = document.createElement('section');
    section.className = "tooltip-generic tooltip-enabled";
    let arrow = document.createElement('span');
    arrow.className = `tooltip-arrow tooltip-arrow-right`;
    let tooltipContent = document.createElement('span');
    tooltipContent.className = "tooltip-content";
    let a = document.createElement('span')
    a.id = 'tooltip';
    a.className = 'tooltip-3d';
    a.style.marginLeft = `${event.offsetX + 15}px`;
    a.style.marginTop = `-${this.scrollOffsetTop + this.dimensions.height - event.offsetY + 15}px`;
    let data1 = []
    if (data.length > 0) {
      data.forEach(content => {
        let contentData = document.createElement('div');
        contentData.innerHTML = `${content.type} : ${content.value} \n`;
        data1.push(contentData);
        if(!hasData) {
          let contentData = document.createElement('div');
          contentData.innerHTML = `No data available`;
          data1.push(contentData);
        }
      });
      data1.forEach(item => {
        tooltipContent.appendChild(item);
      })
    } else
      tooltipContent.innerHTML = `No data available`;
    section.appendChild(arrow);
    section.appendChild(tooltipContent);
    a.appendChild(section);
    this.prostateViewer.nativeElement.appendChild(a);
  }

  private onWheel(event) {
    event.preventDefault();
    // const amount = event.deltaY / 100;
    // const zoom = this.camera.zoom - amount;
    // this.camera.zoom=zoom;
    let max:number=Math.max(Math.abs(this.camera.position.x),Math.abs(this.camera.position.y),Math.abs(this.camera.position.z))
        this.scenes.homunculus.scale.set(max/100,max/100,max/100);
  }
  private toCameraCoordinates(position: THREE.Vector3): THREE.Vector3 {
    return position.applyMatrix4(this.camera.matrixWorldInverse);
  }

  private scaleInView(): void {
    const point1 = this.toCameraCoordinates(this.prostateMeshBoundingBox.min);
    const point2 = this.toCameraCoordinates(this.prostateMeshBoundingBox.max);
    this.camera.fov = ThreeUtil.angleInDegree(point1, point2, this.camera) * 2;
    this.camera.updateProjectionMatrix();
  }

  private createBiopsyMeshes(biopsiesMeshData: BiopsyMeshData[]): THREE.Mesh[] {
    return biopsiesMeshData.map((biopsyMeshData: BiopsyMeshData, index: number) => {
      const startPoint = new THREE.Vector3(
        biopsyMeshData.proximalX,
        biopsyMeshData.proximalY,
        biopsyMeshData.proximalZ
      );
      const color = this.calculateColorCode(biopsyMeshData);
      const endPoint = new THREE.Vector3(biopsyMeshData.distalX, biopsyMeshData.distalY, biopsyMeshData.distalZ);

      const opacity = 0.5;
      const material = new THREE.MeshLambertMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: false,
        emissive: new THREE.Color(color),
        opacity,
      });

      const cylinder = ThreeUtil.getCylinderBetweenPoints(startPoint, endPoint, material);
      cylinder.applyMatrix(this.matrix);
      cylinder.name = `biopsy_${index}`

      return cylinder;
    });
  }

  private calculateColorCode(biopsyMeshData: BiopsyMeshData) {
    const summary = this.summryTable.summary.filter(item => item.target === biopsyMeshData.tooltipConfiguration.tooltipData.lesionID);
    let gleasonScore = null;
    if (has(summary, 0)) {
      gleasonScore = get(summary[0],'overallGleason', null);
    }
    let colorCode = null;
    if (gleasonScore) {
      const overall = gleasonScore.substring(0, gleasonScore.indexOf('('));
      const primary = gleasonScore.substring(gleasonScore.indexOf('(') + 1, gleasonScore.indexOf(','));
      if (gleasonScore === 'Benign')
        colorCode = COLORS_CONFIG.gleason[gleasonScore];
      else if (parseInt(overall) <= 6)
        colorCode = COLORS_CONFIG.gleason['<=6'];
      else if (overall === '7') {
        if(parseInt(primary) >= 4) {
          colorCode = COLORS_CONFIG.gleason['7(4+3)'];
        } else {
          colorCode = COLORS_CONFIG.gleason['7(3+4)'];
        }
      }
      else if (overall === '8')
        colorCode = COLORS_CONFIG.gleason[overall];
      else if (parseInt(overall) >= 9)
        colorCode = COLORS_CONFIG.gleason['>=9'];
    }
    const color = colorCode ?
      colorCode : COLORS_CONFIG.nonAdenocarcinoma.unselected;
    return color;
  }

  private loadRoiMeshesOnScene(roisMeshData: RoiMeshData[]): void {
    roisMeshData.forEach((roiMeshData: RoiMeshData,index:number) => this.setupRoiMesh(roiMeshData,index));
  }

  private setupRoiMesh(roiMeshData: RoiMeshData,index:number): void {
    const loader = new THREESTLLoader(THREE);
    let dataUri=`data:text/plain;base64,${roiMeshData.ROIMeshUrl}`;
    if(roiMeshData.tooltipConfiguration) {
      if(typeof(roiMeshData.tooltipConfiguration)==="string" )
        roiMeshData.tooltipConfiguration = JSON.parse(roiMeshData.tooltipConfiguration);
    }
    else{
      roiMeshData.tooltipConfiguration= {
        tooltipShow: "true",
        tooltipData: null
      }
    }
    loader.prototype.load(dataUri, (roiGeometry: THREE.Geometry) => {
      const color = new this.three.Color(COLORS_CONFIG.scores[`${get(roiMeshData.tooltipConfiguration,"tooltipData.piradsScore",0)}`]);
      const colorVector = new this.three.Vector4(color.r, color.g, color.b, 0);
      const shaderMaterial = ThreeUtil.createShaderMaterial(colorVector, roiMeshData.adenocarcinoma);
      const mesh = new this.three.Mesh(roiGeometry, shaderMaterial);
      mesh.name = `roi_${roiMeshData.ROIId}`;
      roiGeometry.applyMatrix(this.matrix);

      this.addToMainScene(mesh);
      this.renderScenes()
    });
  }

  private setupHomunculusMesh(): void {
     new THREEObjLoader(THREE);
     new this.three.OBJLoader().load(HOMUNCULUS_OBJ_URL, (group: THREE.Group) => {
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // apply custom material
          child.material = new this.three.MeshPhongMaterial({
            color: COLORS_CONFIG.homunculus.base,
            specular: COLORS_CONFIG.homunculus.specular,
            shininess: 100,
            flatShading:false
          });

          const homunculusMatrix = new this.three.Matrix4();
          homunculusMatrix.makeScale(0.020, 0.020, 0.020);
          homunculusMatrix.multiply(this.rotationMatrix);
          child.applyMatrix(homunculusMatrix);
        }
      });

      this.scenes.homunculus.add(group);

    });
  }

  private addToMainScene(object3D: THREE.Object3D): void {
    this.scenes.main.add(object3D);
    // Adding Ambient Light for all scenes inside main scenes except Humanoid
    const ambientLight = new this.three.AmbientLight(0x000000);
    this.scenes.main.add(ambientLight);
  }
  ngOnDestroy(): void {
    if(!this.prostateMeshData.reportData){
      this.prostateMeshData.reportData= this.renderer.domElement.toDataURL('image/png');
      this.updateReport.emit(this.prostateMeshData.reportData);
    }
    window.removeEventListener('scroll',this.scroll);
  }
}
